import { ActionIcon, Badge, Group, Stack, Text } from '@mantine/core';
import { useHover, useMediaQuery, useSessionStorage } from '@mantine/hooks';
import EditIcon from '@/icons/edit.svg';
import { Workflow } from '@/app/workflows/actions/interfaces';
import { NEWLY_CREATED_WORKFLOWS_IDS } from '@/app/workflows/constants';
import { WorkflowIdBadge } from './WorkflowIdBadge';
import { isRpdx7 } from '@/app/workflows/utils/isRpdx7';
import { useGrowthbookContext } from '@/app/contexts/GrowthbookContext';

type NameDescriptionColumnProps = {
  workflow: Workflow;
  getIsRowExpanded?: () => boolean;
  onClick?: () => void;
};

function truncateText(text: string, length: number): string {
  return text.length > length ? text.slice(0, length) + '...' : text;
}

function stripHtml(html: string): string {
  if (typeof document !== 'undefined') {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  } else {
    // If document is not defined, fall back to a regex-based stripping
    return html.replace(/<\/?[^>]+(>|$)/g, '');
  }
}

function containsHtml(str: string): boolean {
  const htmlRegex = /<\/?[a-z][\s\S]*>/i;
  return htmlRegex.test(str);
}

const NameBadges = ({ workflow }: { workflow: Workflow }) => {
  const [value] = useSessionStorage({
    key: NEWLY_CREATED_WORKFLOWS_IDS,
    defaultValue: [] as number[],
  });
  const shouldDisplayNewBadge = value.includes(workflow.id);
  const isSmallerThan868 = useMediaQuery('(max-width: 868px)');

  if (isSmallerThan868) {
    return null;
  }

  if (shouldDisplayNewBadge) {
    return <Badge color='green'>NEW</Badge>;
  }

  return <Badge color='grape'>{workflow.clientMetadata.type}</Badge>;
};

export function NameDescriptionColumn({
  workflow,
  getIsRowExpanded,
  onClick,
  ...mantineHoverCardChildProps
}: NameDescriptionColumnProps & React.ComponentPropsWithoutRef<'div'>) {
  const { ref, hovered } = useHover();
  const matches = useMediaQuery('(max-width: 868px)');

  const isRowExpanded = getIsRowExpanded?.() ?? false;

  const description = containsHtml(workflow.description) ? stripHtml(workflow.description) : workflow.description;
  const truncatedDescription = truncateText(description, matches ? 30 : 60);

  const growthbook = useGrowthbookContext();
  const showLegacyBadge = growthbook?.isOn('new-settings-full-release') && !isRpdx7(workflow.rpdxVersion);

  return (
    <Stack gap={0} ref={ref}>
      <Group gap='xs'>
        <Text fw={600} c='dark.9' {...mantineHoverCardChildProps} style={{ width: 'fit-content' }}>
          {workflow.name}
        </Text>
        {hovered ? (
          <ActionIcon
            style={{
              backgroundColor: 'transparent',
              padding: 0,
            }}
            size='xs'
            onClick={onClick}
          >
            <EditIcon />
          </ActionIcon>
        ) : null}
        <WorkflowIdBadge workflow={workflow} />
        <NameBadges workflow={workflow} />
        {showLegacyBadge && <Badge color='grape'>Legacy</Badge>}
      </Group>
      {isRowExpanded ? null : (
        <Text size='sm' c='gray.6'>
          {truncatedDescription}
        </Text>
      )}
    </Stack>
  );
}
